import React, { useEffect, useMemo , useState} from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, IconButton, 
  Grid, Stack, Divider, Dialog, DialogActions, DialogTitle, DialogContent, 
  List, ListItem, ListItemButton, ListItemText, Avatar, ListItemAvatar, 
  GlobalStyles, Button
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { format, getDaysInMonth } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import * as carApi from '../../lib/api/car';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import { ko } from 'date-fns/locale';


const ITEM_HEIGHT = 45;

const SlotWheel = ({ items, currentValue, onSelect, unit }) => {
  const handleScroll = (e) => {
    const index = Math.round(e.currentTarget.scrollTop / ITEM_HEIGHT);
    if (items[index] !== undefined && items[index] !== currentValue) {
      onSelect(items[index]);
    }
  };

  return (
    <Box sx={{ flex: 1, position: 'relative', height: ITEM_HEIGHT * 3, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ position: 'absolute', top: ITEM_HEIGHT, left: 0, right: 0, height: ITEM_HEIGHT, borderTop: '1px solid #eee', borderBottom: '1px solid #eee', bgcolor: 'rgba(25, 118, 210, 0.04)', pointerEvents: 'none', zIndex: 1 }} />
      <Box onScroll={handleScroll} sx={{ height: '100%', overflowY: 'auto', scrollSnapType: 'y mandatory', '&::-webkit-scrollbar': { display: 'none' }, py: `${ITEM_HEIGHT}px` }}>
        {items.map((item, i) => (
          <Box key={i} sx={{ height: ITEM_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', scrollSnapAlign: 'center' }}>
            <Typography sx={{ fontSize: currentValue === item ? '1.1rem' : '0.85rem', fontWeight: currentValue === item ? 800 : 400, color: currentValue === item ? 'primary.main' : 'text.disabled' }}>
              {String(item).padStart(2, '0')}{unit}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const CarRegForm = ({ 
  formik, isNew, visitList, setVisitList, isListOpen, setIsListOpen, 
  isPartnerSelected, setIsPartnerSelected, partnerDisplayName, setPartnerDisplayName, pickerTarget, setPickerTarget
}) => {
  const navigate = useNavigate();
  const isLocked = Boolean(formik.values.confirm);
  
  const location = useLocation();
  const isEdit = location.state?.type === 'edit';
  const [isMileageBlurred, setIsMileageBlurred] = useState(false);
  const [localReservRows, setLocalReservRows] = useState(location.state?.reservData || []);
  const before = Number(formik.values.beforeMileage) || 0;
  const after = Number(formik.values.afterMileage) || 0;
  const totalMileage = after > 0 ? after - before : 0; // 주행 후 입력 시에만 계산
  const isMileageConfirmed = after > 0 && after >= before;
  const isMileageMismatch = useMemo(() => {
    const before = Number(formik.values.beforeMileage) || 0;
    const after = Number(formik.values.afterMileage) || 0;
    const commute = Number(formik.values.commuteMileage) || 0;
    const business = Number(formik.values.businessMileage) || 0;
    const totalMileage = after - before; // 계기판상 총 주행거리
    const recordedSum = commute + business; // 입력한 용도별 합계

      // 주행 후 거리가 입력되었을 때만 검증 (차이가 0.1 초과인 경우 불일치로 판단)
    return after > 0 && Math.abs(totalMileage - recordedSum) > 0.1;
  }, [formik.values.beforeMileage, formik.values.afterMileage, formik.values.commuteMileage, formik.values.businessMileage]);


  const currentVal = pickerTarget === 'start' ? formik.values.startDate : formik.values.endDate;
  const years = useMemo(() => {
    const yr = new Date().getFullYear();
    return Array.from({ length: 3 }, (_, i) => yr + i);
  }, []);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const daysInMonth = Array.from({ length: getDaysInMonth(currentVal) }, (_, i) => i + 1);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handlePartnerChange = (e) => {
    setPartnerDisplayName(e.target.value);
    setIsPartnerSelected(false);
    formik.setFieldValue('visitPartner', '');
  };

  const handleOpenVisitList = async () => {
    if (!partnerDisplayName?.trim()) return alert("거래처명을 입력하세요.");
    try {
      const res = await carApi.getCarUsageVisitList({ partnerNm: partnerDisplayName });
      setVisitList(res.data?.list || res.data || []);
      setIsListOpen(true);
    } catch (e) { alert("조회 실패"); }
  };

  const handleSelectVisit = (p) => {
    const name = p.partnerNm || p.vendorNm || '';
    formik.setFieldValue('visitPartner', p.partnerUuid ? p.partnerUuid : 'newPlace_' + name);
    setPartnerDisplayName(name);
    setIsPartnerSelected(true);
    setIsListOpen(false);
  };
  useEffect(() => {
    let isSubscribed = true;

    const fetchReservList = async () => {
      try {
        const res = await carApi.getCarReservList({ carUuid: formik.values.carUuid });
        if (isSubscribed) {
          const reversedData = (res?.data?.list || []).reverse().slice(0, 3);
          setLocalReservRows(reversedData);
        }
      } catch (err) {
        if (isSubscribed) setLocalReservRows([]);
      }
    };
    fetchReservList();
    return () => {
      isSubscribed = false;
    };
  }, []);

  return (
    <>
      <GlobalStyles styles={{ 
              body: { backgroundColor: '#F8F9FA !important', margin: 0 },
              html: { backgroundColor: '#F8F9FA !important' } 
            }} />
      <Box sx={{ p: 1, maxWidth: 600, mx: 'auto', minHeight: '100dvh' }}>
        <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 3, position: 'relative' }} alignItems="center" justifyContent="center">
              <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 0 }}><ArrowBackIosNewIcon /></IconButton>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>법인 차량 이용 및 주행 기록</Typography>
            </Stack>

            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6}><TextField fullWidth size="small" label="사용 부서" value={formik.values.dptNm} InputProps={{ readOnly: true }} sx={{ bgcolor: '#f5f5f5' }} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="사용자" value={formik.values.usrNm} InputProps={{ readOnly: true }} sx={{ bgcolor: '#f5f5f5' }} /></Grid>
                
                <Grid item xs={12}>
                  <TextField 
                    fullWidth size="small" label="배차 차량" 
                    value={
                      formik.values.carNumber 
                        ? `${formik.values.carNumber} (${formik.values.carModel || ''}) ${formik.values.parking ? `| 주차: ${formik.values.parking}` : ''}`
                        : ''
                    }
                    InputProps={{ readOnly: true }} sx={{ bgcolor: '#f5f5f5' }} 
                  />
                </Grid>
                
                {formik.values.carUuid && localReservRows.length > 0 && (
                  <Grid item xs={12}>
                    <Stack spacing={1.5} sx={{ mt: -2 }}>
                      {localReservRows.map((res, idx) => {
                        const dateRange = `${format(new Date(res.startDate), 'yyyy-MM-dd(eee) HH:mm', { locale: ko })} ~ ${format(new Date(res.endDate), 'yyyy-MM-dd(eee) HH:mm', { locale: ko })}`;

                        // --- 시간 차이 계산 로직 추가 ---
                        let isUrgent = false;
                        if (idx === 1 && localReservRows[0]) {
                          const prevEnd = new Date(localReservRows[0].endDate).getTime();
                          const nextStart = new Date(res.startDate).getTime();
                          const diffHours = (nextStart - prevEnd) / (1000 * 60 * 60); // 시간 단위 변환
                          if (diffHours >= 0 && diffHours <= 12) isUrgent = true; // 12시간 이내 체크
                        }

                        return (
                          <Box key={idx}>
                            {idx === 0 ? (
                              <Grid item xs={12} sx={{ mt: 2 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="예약 기간"
                                  value={dateRange}
                                  InputProps={{ readOnly: true }}
                                  sx={{
                                    bgcolor: '#f5f5f5',
                                    '& .MuiInputBase-input': { fontSize: '0.8rem', fontWeight: 700, color: '#1976d2', WebkitTextFillColor: '#1976d2' }
                                  }}
                                />
                              </Grid>
                            ) : (
                              <>
                                {idx === 1 && (
                                  <Typography variant="caption" sx={{ fontWeight: 800, color: isUrgent ? 'error.main' : 'primary.main', ml: 0.5, display: 'block', mb: 0.5, mt: 1 }}>
                                    {isUrgent ? '⚠️ 24시간 이내 예약 대기' : '다음 예약 대기 현황'}
                                  </Typography>
                                )}
                                <Card 
                                  variant="outlined" 
                                  sx={{ 
                                    borderRadius: 2, 
                                    // 12시간 이내면 빨간색, 아니면 파란색 테두리
                                    borderColor: isUrgent ? '#d32f2f' : '#1976d2',
                                    borderWidth: isUrgent ? 2 : 1,
                                    bgcolor: isUrgent ? '#fffbfa' : '#fff'
                                  }}
                                >
                                  <Box sx={{ p: 1.2 }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, color: isUrgent ? '#000000' : 'text.primary' }}>
                                        {res.usrNm} ({res.dptNm})
                                      </Typography>
                                      <Box sx={{ 
                                        px: 1, py: 0.2, borderRadius: 1, fontSize: '0.65rem', fontWeight: 900, 
                                        bgcolor: isUrgent ? '#d32f2f' : '#E3F2FD', 
                                        color: isUrgent ? '#fff' : '#1976D2' 
                                      }}>
                                        예약
                                      </Box>
                                    </Stack>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: isUrgent ? '#d32f2f' : 'primary.main', lineHeight: 1.4 }}>
                                      {dateRange}
                                    </Typography>
                                    
                                    {/* 긴급 안내 문구 추가 */}
                                    {isUrgent && (
                                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#d32f2f', mt: 0.5 }}>
                                        * 다음 예약 사용자가 있으니 차량 반납 시간을 엄수해 주세요.
                                      </Typography>
                                    )}
                                  </Box>
                                </Card>
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField 
                    fullWidth size="small" label="사용 시작 일시" 
                    value={format(formik.values.startDate, 'yyyy-MM-dd HH:mm')} 
                    InputProps={{ readOnly: true }} sx={{ bgcolor: '#f5f5f5' }} 
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="방문 거래처" value={partnerDisplayName} onChange={handlePartnerChange} disabled={isEdit}
                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: isEdit ? '#f5f5f5' : (isPartnerSelected ? '#f0f7ff' : 'inherit') } }}
                    InputProps={{ endAdornment: (
                      <IconButton size="small" onClick={handleOpenVisitList} disabled={isEdit}>
                        {isPartnerSelected ? <CheckCircleIcon color={isEdit ? "disabled" : "success"} /> : <SearchIcon />}
                      </IconButton>
                    ) }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField 
                    fullWidth size="small" label="주행 전 계기판 거리 (km)" name="beforeMileage" 
                    type="number" value={formik.values.beforeMileage} onChange={formik.handleChange} 
                    InputProps={{ readOnly: !isNew }} 
                  />
                </Grid>

                {!isNew && (
                  <>
                    <Grid item xs={12} sx={{ mt: 2, mb: 1 }}><Divider><Typography variant="caption" sx={{ fontWeight: 800 }}>운행 상세 기록</Typography></Divider></Grid>
                    
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small" 
                        label="주행 후 계기판 거리 (km)" 
                        name="afterMileage" 
                        type="number" 
                        value={formik.values.afterMileage} 
                        onChange={formik.handleChange} 
                        InputProps={{ readOnly: isLocked }}
                        // 에러 발생 시 빨간색 테두리 표시
                        error={formik.touched.afterMileage && Boolean(formik.errors.afterMileage)}
                        // 에러 메시지 텍스트 표시
                        helperText={formik.touched.afterMileage && formik.errors.afterMileage}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField 
                        fullWidth size="small" label="사용 마무리 일시" 
                        value={format(formik.values.endDate, 'yyyy-MM-dd HH:mm')} 
                        InputProps={{ readOnly: true }} sx={{ bgcolor: '#f5f5f5' }} 
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <TextField fullWidth size="small" label="출퇴근(km)" name="commuteMileage" type="number" value={formik.values.commuteMileage} onChange={formik.handleChange} onBlur={() => setIsMileageBlurred(true)} InputProps={{ readOnly: isLocked }} />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField fullWidth size="small" label="업무용(km)" name="businessMileage" type="number" value={formik.values.businessMileage} onChange={formik.handleChange} onBlur={() => setIsMileageBlurred(true)} InputProps={{ readOnly: isLocked }} />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField 
                        fullWidth size="small" label="총 주행거리" 
                        // 값이 확정된 경우에만 수치 표시, 아니면 빈칸
                        value={isMileageConfirmed ? `${totalMileage.toLocaleString()}` : ''} 
                        InputProps={{ readOnly: true }} 
                        sx={{ 
                          bgcolor: isMileageConfirmed ? '#f8f9fa' : '#fff',
                          '& input': { 
                            fontWeight: 800, 
                            color: isMileageMismatch ? 'error.main' : 'success.main', 
                            textAlign: 'center' 
                          } 
                        }} 
                        // 값이 확정되었을 때만 일치 여부 표시
                        helperText={isMileageConfirmed && isMileageBlurred ? (
                          <Typography variant="caption" sx={{ 
                            color: isMileageMismatch ? 'error.main' : 'success.main', 
                            fontWeight: 900,
                            fontSize: '0.65rem'
                          }}>
                            
                          </Typography>
                        ) : null}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth select size="small" label="세차여부" name="washCar" value={formik.values.washCar || 'N'} onChange={formik.handleChange} InputProps={{ readOnly: isLocked }}>
                        <MenuItem value="N">N</MenuItem>
                        <MenuItem value="Y">Y</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="주유비 (원)" name="oilFee" type="number" value={formik.values.oilFee || ''} onChange={formik.handleChange} InputProps={{ readOnly: isLocked }} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField fullWidth select size="small" label="차량이상 유무" name="carIssue" value={formik.values.carIssue || 'N'} onChange={formik.handleChange} InputProps={{ readOnly: isLocked }}>
                        <MenuItem value="N">무</MenuItem>
                        <MenuItem value="Y">유</MenuItem>
                      </TextField>
                    </Grid>
                    {formik.values.carIssue === 'Y' && (
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={2} size="small" label="차량 이상 상세 내용" name="carIssueDetail" value={formik.values.carIssueDetail || ''} onChange={formik.handleChange} InputProps={{ readOnly: isLocked }} />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={2} size="small" label="기타사항" InputLabelProps={{ shrink: true }} placeholder="경유지만 입력하세요" name="comment" value={formik.values.comment || ''} onChange={formik.handleChange} InputProps={{ readOnly: isLocked }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField 
                            fullWidth 
                            select 
                            size="small" 
                            label="차량 운행 여부" 
                            name="returnYn"
                            value={formik.values.returnYn || 'N'} 
                            onChange={(e) => {
                              const { value } = e.target;
                              formik.setFieldValue('returnYn', value);
                              
                              // 'N'(운행하기) 선택 시 주차위치 초기화
                              if (value === 'N') {
                                formik.setFieldValue('parking', '');
                              }
                            }} 
                            InputProps={{ readOnly: isLocked }}
                            error={formik.touched.returnYn && Boolean(formik.errors.returnYn)}
                            helperText={formik.touched.returnYn && formik.errors.returnYn}
                          >
                            <MenuItem value="Y">반납하기</MenuItem>
                            <MenuItem value="N">운행하기</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid item xs={6}>
                          <TextField 
                            fullWidth 
                            select 
                            size="small" 
                            label="주차위치" 
                            name="parking" 
                            value={formik.values.parking || ''} 
                            onChange={formik.handleChange} 
                            // returnYn이 'Y'가 아니면 비활성화 (disabled)
                            disabled={formik.values.returnYn !== 'Y'}
                            InputProps={{ readOnly: isLocked }}
                            error={formik.touched.parking && Boolean(formik.errors.parking)}
                            helperText={formik.touched.parking && formik.errors.parking}
                          >
                            <MenuItem value="B1">B1</MenuItem>
                            <MenuItem value="B2">B2</MenuItem>
                            <MenuItem value="B3">B3</MenuItem>
                            <MenuItem value="B4">B4</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>

              <Button 
                fullWidth type="submit" variant="contained" size="large" 
                startIcon={<SaveIcon />} 
                disabled={isLocked || (!isNew && isMileageMismatch)}
                sx={{ 
                  mt: 4, height: 54, fontWeight: 800, borderRadius: 2,
                  bgcolor: (isLocked || (!isNew && isMileageMismatch)) ? '#ccc !important' : '#1976d2 !important',
                  color: '#fff !important'
                }}
              >
                {isNew ? '운행 전 기록 등록' 
                : formik.values.returnYn === 'Y' 
                ? '차량 반납 하기' 
                : '운행 후 기록 등록'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Dialog open={Boolean(pickerTarget)} onClose={() => setPickerTarget(null)} fullWidth maxWidth="xs">
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800 }}>날짜 및 시간 선택</Typography>
            <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
              <SlotWheel items={years} currentValue={currentVal.getFullYear()} onSelect={(v) => { const d = new Date(currentVal); d.setFullYear(v); formik.setFieldValue(pickerTarget === 'start' ? 'startDate' : 'endDate', d); }} unit="년" />
              <SlotWheel items={months} currentValue={currentVal.getMonth() + 1} onSelect={(v) => { const d = new Date(currentVal); d.setMonth(v - 1); formik.setFieldValue(pickerTarget === 'start' ? 'startDate' : 'endDate', d); }} unit="월" />
              <SlotWheel items={daysInMonth} currentValue={currentVal.getDate()} onSelect={(v) => { const d = new Date(currentVal); d.setDate(v); formik.setFieldValue(pickerTarget === 'start' ? 'startDate' : 'endDate', d); }} unit="일" />
            </Stack>
            <Stack direction="row" spacing={1}>
              <SlotWheel items={hours} currentValue={currentVal.getHours()} onSelect={(v) => { const d = new Date(currentVal); d.setHours(v); formik.setFieldValue(pickerTarget === 'start' ? 'startDate' : 'endDate', d); }} unit="시" />
              <SlotWheel items={minutes} currentValue={currentVal.getMinutes()} onSelect={(v) => { const d = new Date(currentVal); d.setMinutes(v); formik.setFieldValue(pickerTarget === 'start' ? 'startDate' : 'endDate', d); }} unit="분" />
            </Stack>
            <Button fullWidth variant="contained" onClick={() => setPickerTarget(null)} sx={{ mt: 3, borderRadius: 2 }}>확인</Button>
          </Box>
        </Dialog>
        <Dialog open={isListOpen} onClose={() => setIsListOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2, borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', fontSize: '1rem' }}>거래처 검색 결과</DialogTitle>
          <DialogContent dividers sx={{ p: 0, maxHeight: '50vh' }}>
            <List>
              {visitList.length > 0 ? visitList.map((item, index) => (
                <ListItem key={index} disablePadding divider>
                  <ListItemButton onClick={() => handleSelectVisit(item)} sx={{ py: 1.5 }}>
                    <ListItemAvatar><Avatar sx={{ width: 32, height: 32 }}><BusinessIcon sx={{ fontSize: 18 }} /></Avatar></ListItemAvatar>
                    <ListItemText primary={item.partnerNm || item.vendorNm} secondary={item.partnerAddr} primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }} secondaryTypographyProps={{ fontSize: '0.75rem' }} />
                  </ListItemButton>
                </ListItem>
              )) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>"{partnerDisplayName}"을(를) 등록할까요?</Typography>
                  <Stack direction="row" spacing={1}><Button fullWidth variant="contained" onClick={() => handleSelectVisit({ partnerNm: partnerDisplayName })}>예</Button><Button fullWidth variant="outlined" onClick={() => setIsListOpen(false)}>아니오</Button></Stack>
                </Box>
              )}
            </List>
          </DialogContent>
          <DialogActions><Button onClick={() => setIsListOpen(false)} fullWidth color="inherit">닫기</Button></DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CarRegForm;
